import React, { useState, useRef, useCallback } from 'react';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { Button } from './ui/button';
import { Crop, RotateCw, ZoomIn, ZoomOut, Check, X, Move } from 'lucide-react';

/**
 * ImageCropper - Allows users to crop and resize images before upload
 * 
 * Props:
 * - imageSrc: Source image URL or base64
 * - aspectRatio: Desired aspect ratio (e.g., 16/9 for banners)
 * - onCropComplete: Callback with cropped image blob/base64
 * - onCancel: Callback when user cancels
 */
const ImageCropper = ({ 
  imageSrc, 
  aspectRatio = 16 / 9,  // Default banner aspect ratio
  onCropComplete, 
  onCancel,
  cropShape = 'rect',  // 'rect' or 'round'
  transparentBackground = false,  // If true, outputs PNG with transparency
  title = "Crop & Resize Image"  // Custom title for the modal
}) => {
  const [crop, setCrop] = useState({
    unit: '%',
    width: 90,
    height: 90 / aspectRatio,
    x: 5,
    y: 5
  });
  const [completedCrop, setCompletedCrop] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const imgRef = useRef(null);
  const canvasRef = useRef(null);

  const onImageLoad = useCallback((e) => {
    const { width, height } = e.currentTarget;
    
    // Calculate initial crop to fill the aspect ratio
    let cropWidthPercent = 90;
    let cropHeightPercent = cropWidthPercent / aspectRatio;
    
    if (cropHeightPercent > 90) {
      cropHeightPercent = 90;
      cropWidthPercent = cropHeightPercent * aspectRatio;
    }
    
    const initialCrop = {
      unit: '%',
      width: cropWidthPercent,
      height: cropHeightPercent,
      x: (100 - cropWidthPercent) / 2,
      y: (100 - cropHeightPercent) / 2
    };
    
    setCrop(initialCrop);
    
    // Also set completedCrop with pixel values so Apply Crop works immediately
    const pixelCrop = {
      unit: 'px',
      width: (cropWidthPercent / 100) * width,
      height: (cropHeightPercent / 100) * height,
      x: ((100 - cropWidthPercent) / 2 / 100) * width,
      y: ((100 - cropHeightPercent) / 2 / 100) * height
    };
    setCompletedCrop(pixelCrop);
  }, [aspectRatio]);

  const getCroppedImg = useCallback(async () => {
    if (!completedCrop || !imgRef.current || !canvasRef.current) {
      return;
    }

    const image = imgRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    if (!ctx) return;

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    // Set canvas size to the cropped area
    const cropX = completedCrop.x * scaleX;
    const cropY = completedCrop.y * scaleY;
    const cropWidth = completedCrop.width * scaleX;
    const cropHeight = completedCrop.height * scaleY;

    // Output size - maintain reasonable resolution
    const maxWidth = 1920;
    const maxHeight = 1080;
    
    let outputWidth = cropWidth;
    let outputHeight = cropHeight;
    
    if (outputWidth > maxWidth) {
      outputHeight = (maxWidth / outputWidth) * outputHeight;
      outputWidth = maxWidth;
    }
    if (outputHeight > maxHeight) {
      outputWidth = (maxHeight / outputHeight) * outputWidth;
      outputHeight = maxHeight;
    }

    canvas.width = outputWidth;
    canvas.height = outputHeight;

    // Apply rotation if needed
    if (rotation !== 0) {
      ctx.save();
      ctx.translate(outputWidth / 2, outputHeight / 2);
      ctx.rotate((rotation * Math.PI) / 180);
      ctx.translate(-outputWidth / 2, -outputHeight / 2);
    }

    ctx.drawImage(
      image,
      cropX,
      cropY,
      cropWidth,
      cropHeight,
      0,
      0,
      outputWidth,
      outputHeight
    );

    if (rotation !== 0) {
      ctx.restore();
    }

    // Convert canvas to blob - use PNG for transparent backgrounds, JPEG otherwise
    const mimeType = transparentBackground ? 'image/png' : 'image/jpeg';
    const fileExtension = transparentBackground ? 'png' : 'jpg';
    const quality = transparentBackground ? undefined : 0.9;  // PNG doesn't use quality
    
    return new Promise((resolve) => {
      canvas.toBlob(
        (blob) => {
          if (blob) {
            // Create a File from the blob
            const file = new File([blob], `cropped-image.${fileExtension}`, { type: mimeType });
            resolve(file);
          }
        },
        mimeType,
        quality
      );
    });
  }, [completedCrop, rotation, transparentBackground]);

  const handleComplete = async () => {
    const croppedFile = await getCroppedImg();
    if (croppedFile && onCropComplete) {
      onCropComplete(croppedFile);
    }
  };

  const handleRotate = () => {
    setRotation((prev) => (prev + 90) % 360);
  };

  return (
    <div 
      className="sh-modal-overlay fixed inset-0 z-[9999] flex items-center justify-center p-4"
      style={{ 
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        isolation: 'isolate',
        pointerEvents: 'auto'
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div 
        className="sh-modal-surface sh-modal-cropper bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        style={{ pointerEvents: 'auto' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sh-modal-header p-4 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Crop className="h-5 w-5 text-indigo-600" />
            <h3 className="sh-modal-title font-semibold text-slate-900">{title}</h3>
          </div>
          <p className="text-sm text-slate-500">
            Drag to reposition, resize handles to adjust
          </p>
        </div>

        {/* Crop Area */}
        <div className="flex-1 overflow-auto p-4 bg-slate-100 flex items-center justify-center">
          <div style={{ transform: `scale(${zoom})`, transition: 'transform 0.2s' }}>
            <ReactCrop
              crop={crop}
              onChange={(c) => setCrop(c)}
              onComplete={(c) => setCompletedCrop(c)}
              aspect={aspectRatio}
              circularCrop={cropShape === 'round'}
            >
              <img
                ref={imgRef}
                src={imageSrc}
                alt="Crop preview"
                onLoad={onImageLoad}
                style={{ 
                  maxHeight: '60vh', 
                  transform: `rotate(${rotation}deg)`,
                  transition: 'transform 0.3s'
                }}
              />
            </ReactCrop>
          </div>
        </div>

        {/* Controls */}
        <div className="sh-modal-footer p-4 border-t border-slate-200 bg-white">
          <div className="flex items-center justify-between">
            {/* Zoom & Rotate Controls */}
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setZoom(Math.max(0.5, zoom - 0.1))}
                title="Zoom Out"
              >
                <ZoomOut className="h-4 w-4" />
              </Button>
              <span className="text-sm text-slate-600 w-16 text-center">
                {Math.round(zoom * 100)}%
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setZoom(Math.min(3, zoom + 0.1))}
                title="Zoom In"
              >
                <ZoomIn className="h-4 w-4" />
              </Button>
              <div className="w-px h-6 bg-slate-300 mx-2" />
              <Button
                variant="outline"
                size="sm"
                onClick={handleRotate}
                title="Rotate 90°"
              >
                <RotateCw className="h-4 w-4" />
              </Button>
            </div>

            {/* Aspect Ratio Info */}
            <div className="text-sm text-slate-500 flex items-center gap-2">
              <Move className="h-4 w-4" />
              <span>
                {aspectRatio === 16/9 && "16:9 Banner"}
                {aspectRatio === 4/3 && "4:3 Standard"}
                {aspectRatio === 1 && "1:1 Square"}
                {aspectRatio === 3/2 && "3:2 Photo"}
              </span>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={onCancel}>
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
              <Button onClick={handleComplete} className="bg-indigo-600 hover:bg-indigo-700">
                <Check className="h-4 w-4 mr-2" />
                Apply Crop
              </Button>
            </div>
          </div>
        </div>

        {/* Hidden canvas for cropping */}
        <canvas ref={canvasRef} style={{ display: 'none' }} />
      </div>
    </div>
  );
};

export default ImageCropper;
